import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Clock, Phone, Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId, whatsappUrl } from "@/utils";

const BROKEN_IMG = "/assets/day-crack.png";
const PRISTINE_IMG =
  "https://images.pexels.com/photos/12467724/pexels-photo-12467724.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden pb-2 -mb-2 ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Crack = () => (
  <svg viewBox="0 0 1200 800" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <motion.path
      d="M -20 430 L 160 405 L 255 445 L 380 385 L 480 430 L 620 365 L 720 420 L 880 355 L 990 405 L 1230 365"
      fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"
      style={{ filter: "drop-shadow(0 0 6px rgba(212,175,55,0.7))" }}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.85 }}
      transition={{ pathLength: { duration: 1.8, delay: 1.1, ease: "easeInOut" }, opacity: { duration: 0.3, delay: 1.1 } }}
    />
    <motion.path
      d="M 380 385 L 410 300 L 455 250"
      fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"
      style={{ filter: "drop-shadow(0 0 4px rgba(212,175,55,0.6))" }}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.6 }}
      transition={{ pathLength: { duration: 0.9, delay: 1.9, ease: "easeOut" }, opacity: { duration: 0.3, delay: 1.9 } }}
    />
    <motion.path
      d="M 620 365 L 660 460 L 705 505"
      fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"
      style={{ filter: "drop-shadow(0 0 4px rgba(212,175,55,0.6))" }}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.6 }}
      transition={{ pathLength: { duration: 0.9, delay: 2.2, ease: "easeOut" }, opacity: { duration: 0.3, delay: 2.2 } }}
    />
    <motion.circle
      cx="160" cy="405" r="10" fill="none" stroke="#D4AF37" strokeWidth="2"
      initial={{ scale: 0, opacity: 0.9 }}
      animate={{ scale: 3, opacity: 0 }}
      transition={{ duration: 1.2, delay: 1.05, ease: "easeOut" }}
      style={{ transformOrigin: "160px 405px" }}
    />
  </svg>
);

export const Hero = () => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = !soundOn;
  }, [soundOn]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!soundOn) {
      v.volume = 0.55;
      v.play().catch(() => {});
    }
    setSoundOn(!soundOn);
  };
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const bgX = useTransform(sx, (v) => -v * 26);
  const bgY = useTransform(sy, (v) => -v * 16);
  const fgX = useTransform(sx, (v) => v * 10);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  const info = [
    { icon: MapPin, text: t.bar.address },
    { icon: Clock, text: t.bar.hours },
    { icon: Phone, text: t.bar.phone },
  ];

  return (
    <section id="top" onMouseMove={onMouseMove} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-[-60px]" style={{ x: bgX, y: bgY }}>
        <motion.img
          src={BROKEN_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.06 }}
          transition={{ duration: 9, ease: "easeOut" }}
        />
        <Crack />
        <motion.div
          className="absolute inset-0"
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.7, delay: 2.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <img src={PRISTINE_IMG} alt="" className="w-full h-full object-cover" />
        </motion.div>
        <video
          ref={(el) => {
            videoRef.current = el;
          }}
          autoPlay
          loop
          playsInline
          onCanPlay={(e) => { e.currentTarget.style.opacity = "1"; }}
          className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-1000"
          data-testid="hero-video"
        >
          <source src="/assets/hero-video.webm" type="video/webm" />
          <source src="/assets/hero-video.mp4" type="video/mp4" />
        </video>
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F6] via-[#FAF9F6]/45 to-[#FAF9F6]/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6]/85 via-[#FAF9F6]/25 to-transparent" />

      <div className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-16">
        <motion.div style={{ x: fgX }} className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-[#B8860B]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#B8860B]">{t.hero.eyebrow}</span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
            <MaskedLine delay={0.35} className="text-[#111111]">{t.hero.lines[0]}</MaskedLine>
            <MaskedLine delay={0.55} className="text-[#111111]">{t.hero.lines[1]}</MaskedLine>
            <MaskedLine delay={0.75} className="text-[#D4AF37]">{t.hero.lines[2]}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-8 text-base sm:text-lg text-black/70 leading-relaxed max-w-xl"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#quote")}
              data-testid="hero-quote-cta"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-bold px-8 py-4 rounded-full text-base transition-colors duration-300 hover:scale-105 transform"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="w-5 h-5" />
            </button>
            <a
              href={whatsappUrl(t.whatsapp.prefill)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2 border border-black/25 hover:border-[#25D366] text-black font-semibold px-8 py-4 rounded-full text-base transition-colors duration-300 hover:text-[#25D366]"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
            className="mt-6 text-xs tracking-[0.2em] uppercase text-black/40"
          >
            {t.hero.note}
          </motion.p>
        </motion.div>
      </div>

      <motion.button
        onClick={toggleSound}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6 }}
        className="absolute bottom-24 right-6 sm:right-8 z-20 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/25 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
        aria-label="Toggle hero sound"
        data-testid="hero-sound-toggle"
      >
        {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.9 }}
        className="relative z-10 border-t border-black/10 bg-white/75 backdrop-blur-md"
        data-testid="hero-info-bar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {info.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3 text-sm text-black/70">
              <Icon className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
